import type { StockLevelResource } from '@repo/api-client';

/**
 * How many of a thing the records say an outlet holds, written the way it has to
 * be read.
 *
 * Two figures are easy to lose on the way to a screen and each loses a different
 * fact, so both are answered here rather than in a cell:
 *
 * - **`0` is a row.** The outlet stocks the item and holds none of it. An item
 *   the outlet does *not* stock is absent from the response entirely (FR-510),
 *   so rendering zero as an empty cell — or filtering the row out — collapses
 *   "we are out of these" into "we do not sell these", and those have different
 *   remedies. Nothing in this module or the screen above it synthesises a row
 *   for an absent item either: the list is the API's list.
 * - **A negative level is real** (CL-501, FR-537b). The API never refuses a sale
 *   for want of stock and never clamps a level, so `−2` means the records and the
 *   shelf disagree by two — usually a delivery nobody entered. It is shown as it
 *   stands. Not clamped to zero, not hidden, not an error: surfacing it is the
 *   entire point of the screen, and a stocktake is what settles it.
 *
 * The API sends the rows **by code** and nothing here reorders them. Sorting
 * negatives to the top would put the exceptions first and read well; it would
 * also mean a manager working down a printed shelf list is reading a different
 * order every time the position changes. The count in the rail is how the
 * exceptions are found without scanning.
 */

/** Indian digit grouping — 1,00,000 rather than 100,000, as `money.ts` uses. */
const GROUPING = new Intl.NumberFormat('en-IN');

/**
 * A level as somebody reads it: `57`, `0`, `−2`.
 *
 * A **true minus sign** and not a hyphen, matching `formatPaise`. It is the width
 * of a digit in Geist's tabular figures, so a column of levels stays aligned
 * whether or not any of them are negative, and it does not read as a dash or a
 * stray mark on a dense table. Screen readers say "minus" for it.
 *
 * `Math.round` guards the one thing the type cannot: `level` is `number` in the
 * generated model, and a fractional value arriving there would otherwise render
 * as `2.5` in a column of counts. A level is a signed sum of whole movements, so
 * a fraction is a contract violation rather than a quantity — this renders the
 * nearest whole number rather than inventing a decimal column for it.
 */
export function formatLevel(level: number): string {
  const whole = Math.round(level);

  return `${whole < 0 ? '−' : ''}${GROUPING.format(Math.abs(whole))}`;
}

/**
 * Whether this row is the one the screen is flagging.
 *
 * A predicate rather than `level < 0` at three call sites, because the count in
 * the rail, the badge on the row and the sentence explaining what the badge
 * means must agree about what "below zero" is. Zero itself is **not** flagged: an
 * outlet that has sold out of something is an outlet doing its job.
 */
export const isBelowZero = (level: number): boolean => level < 0;

/** How many rows are below zero — the rail's count, from the same predicate. */
export const countBelowZero = (items: readonly StockLevelResource[]): number =>
  items.filter((item) => isBelowZero(item.level)).length;
